import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { formatDate } from 'date-fns'
import { CheckCircle, ConstructionIcon, ExternalLink } from 'lucide-react'
import { Button } from '#app/components/ui/button.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.ts'
import { getVisibleGrantsCount } from '#app/utils/grants.server.js'
import { stripe } from '#app/utils/stripe.server.tsx'
import {
	STRIPE_PRICING_TABLE_ID,
	STRIPE_PUBLISHABLE_KEY,
} from '#app/utils/stripe.ts'

declare global {
	namespace JSX {
		interface IntrinsicElements {
			'stripe-pricing-table': React.DetailedHTMLProps<
				React.HTMLAttributes<HTMLElement>,
				HTMLElement
			> & {
				'pricing-table-id': string
				'publishable-key': string
			}
		}
	}
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)

	const grantsCount = await getVisibleGrantsCount()

	if (userId) {
		const user = await prisma.user.findUniqueOrThrow({
			where: {
				id: userId,
			},
			include: {
				BillingSubscription: true,
			},
		})

		if (user.stripeCustomerId) {
			const customerClientSecret = await stripe.customerSessions.create({
				customer: user.stripeCustomerId,
				components: {
					pricing_table: {
						enabled: true,
					},
				},
			})

			return json({
				user,
				grantsCount,
				customerClientSecret: customerClientSecret.client_secret,
			})
		}

		return json({ user, grantsCount, customerClientSecret: null })
	}

	return json({ user: null, grantsCount, customerClientSecret: null })
}

export default function Pricing() {
	const { user, grantsCount, customerClientSecret } =
		useLoaderData<typeof loader>()

	const activeSubscription = user?.BillingSubscription.find(
		subscription =>
			subscription.status === 'active' || subscription.status === 'trialing',
	)

	const cancelledSubscription = user?.BillingSubscription.find(
		subscription => subscription.status === 'canceled',
	)

	return (
		<div className="container mx-auto p-4">
			<h1 className="mb-8 text-center text-3xl font-bold">DoGood Grants</h1>
			{!activeSubscription && (
				<p className="my-2 text-center text-lg">
					Access information on {grantsCount} grants.
				</p>
			)}
			{activeSubscription && activeSubscription.status === 'trialing' && (
				<div className="flex flex-col items-center justify-center">
					<CheckCircle className="h-12 w-12 text-center text-green-500" />
					<p className="my-2 text-center text-2xl">Enjoy your free trial!</p>
					<div className="my- text-center text-lg">
						{activeSubscription.canceledAtPeriodEnd && (
							<p>
								You've elected to cancel your subscription at the end of your
								trial period.
							</p>
						)}
						{!activeSubscription.canceledAtPeriodEnd && (
							<p>
								You will be charged for the subscription after the trial period
								ends{' '}
								{activeSubscription.trialEndsAt
									? `on ${formatDate(activeSubscription.trialEndsAt, 'MM/dd/yyyy')}`
									: ''}
								.
							</p>
						)}
						<br />
						Update or cancel your subscription at any time using the link below.
					</div>
				</div>
			)}
			{activeSubscription && activeSubscription.status === 'active' && (
				<div className="flex flex-col items-center justify-center">
					<CheckCircle className="h-12 w-12 text-center text-green-500" />
					<p className="my-2 text-center text-lg">
						Your subscription to DoGood Grants is active. <br />
						Update or cancel your subscription at any time using the link below.
					</p>
				</div>
			)}
			{cancelledSubscription && !activeSubscription && !user?.bypassPaywall ? (
				<div className="flex flex-col items-center justify-center">
					<ConstructionIcon className="h-12 w-12 text-center text-red-500" />
					<p className="my-2 text-center text-2xl">
						Your subscription has been canceled.
					</p>
					<p className="my-2 text-center text-lg">
						You no longer have access to the full features of the website.
						Select your plan below to resume your access.
					</p>
				</div>
			) : null}

			{user?.bypassPaywall && (
				<div className="flex flex-col items-center justify-center">
					<CheckCircle className="h-12 w-12 text-center text-green-500" />
					<p className="my-2 text-center text-2xl">
						You have access to the full features of the website.
					</p>
				</div>
			)}

			{user && activeSubscription && (
				<p className="my-4 text-center text-lg">
					<Button variant={'primary'} asChild>
						<Link className="text-white" to="/api/billing-portal">
							Manage Subscription <ExternalLink className="ml-2 h-4 w-4" />
						</Link>
					</Button>
				</p>
			)}
			{!activeSubscription || cancelledSubscription ? (
				<>
					<stripe-pricing-table
						client-reference-id={user?.id}
						pricing-table-id={STRIPE_PRICING_TABLE_ID}
						publishable-key={STRIPE_PUBLISHABLE_KEY}
						customer-session-client-secret={customerClientSecret}
					></stripe-pricing-table>
				</>
			) : null}
		</div>
	)
}
